import {
	ChangeDetectionStrategy,
	Component,
	DestroyRef,
	ElementRef,
	HostListener,
	Inject,
	OnInit,
	WritableSignal,
	inject,
	signal,
} from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CmmsFiltersService } from '@app/pages/cmms/components/cmms-sort-filters-grouping/services/cmms-filters.service';
import { DialogRef } from '@angular/cdk/dialog';
import { MatIconModule } from '@angular/material/icon';
import { SearchBarComponent } from '@app/pages/cmms/components/cmms-sort-filters-grouping/search-bar/search-bar.component';
import { CommonModule } from '@angular/common';
import { ActionCenterService } from '@app/core/services/action-center.service';
import { ActionCenterOverlayComponent } from '@app/shared/action-center/components/action-center-overlay/action-center-overlay.component';
import { ActionTypes, IActionConfig } from '@app/shared/action-center/interfaces';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { decrementIndex, incrementIndex } from '@app/core/utils/list-navigation-utils';
import { ICmmsEntitiesList } from '@app/pages/cmms/components/table-elements/interfaces/interfaces';
import { ActionCenterLinkEntitiesDirective } from '@app/shared/directives/action-center-link-entities.directive';
import { ActionCenterFooterComponent } from '@app/shared/action-center/action-center-footer/action-center-footer.component';
import { NgxTolgeeModule } from '@tolgee/ngx';

@Component({
	selector: 'action-center',
	templateUrl: './action-center.component.html',
	styleUrls: ['./action-center.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [CmmsFiltersService],
	imports: [
		MatIconModule,
		SearchBarComponent,
		CommonModule,
		ActionCenterOverlayComponent,
		MatButtonModule,
		ActionCenterLinkEntitiesDirective,
		ActionCenterFooterComponent,
		NgxTolgeeModule,
	],
})
export class ActionCenterComponent implements OnInit {
	private readonly dialogRef = inject(DialogRef<ActionCenterComponent>);
	private readonly actionCenterService = inject(ActionCenterService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly el = inject(ElementRef);

	configs: IActionConfig[];
	configsHotkeyMap: Map<string, IActionConfig> = new Map();
	implemented: boolean = false;
	activeConfig: WritableSignal<IActionConfig> = signal(null);
	lastConfig: WritableSignal<IActionConfig> = signal(null);
	index = signal(0);

	constructor(
		@Inject(MAT_DIALOG_DATA)
		private data: {
			configs: IActionConfig[];
			selectedEntities: ICmmsEntitiesList;
			setActiveConfig: ActionTypes;
		}
	) {}
	ngOnInit(): void {
		this.configs = this.data.configs;
		if (this.data.setActiveConfig) {
			this.activeConfig.set(
				this.configs.find(config => config.name === this.data.setActiveConfig)
			);
		}

		this.configs.forEach(config => {
			if (!config.hotKey) {
				return;
			}
			this.configsHotkeyMap.set(config.hotKey.key, config);
		});
		this.actionCenterService.selectedEntities = this.data.selectedEntities;
		this.closeActionCenterSubscription();
	}

	closeActionCenterSubscription() {
		this.actionCenterService.closeActionCenter$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				this.dialogRef.close();
			});
	}

	selectAction(activeConfig: IActionConfig) {
		this.activeConfig.set(activeConfig);
		this.lastConfig.set(activeConfig);
	}

	getSelectionListChecked(selectedAction: any) {
		const config: IActionConfig[] = selectedAction.map(o => o.value);
		this.selectAction(config[0]);
	}

	closeDialog() {
		this.dialogRef.close();
	}

	async applyAction() {
		this.actionCenterService.applyAction$.next();
	}

	@HostListener('document:keydown.ArrowLeft')
	goBackToActionMenu() {
		if (!this.activeConfig()) {
			return;
		}
		this.activeConfig.set(null);
	}

	@HostListener('document:keydown.ArrowRight')
	goBackToAction() {
		if (this.activeConfig()) {
			return;
		}
		this.activeConfig.set(this.lastConfig());
	}

	@HostListener('window:keydown', ['$event'])
	hotKeys(event: KeyboardEvent) {
		if (event.key === 'Enter' && !!this.activeConfig()) {
			this.applyAction();
		}
		if (this.activeConfig()) {
			return;
		}
		setTimeout(() => {
			this.scrollToActiveElement();
		}, 100);
		if (event.key === 'ArrowDown') {
			this.index.set(incrementIndex(this.index(), this.configs.length));
			return;
		} else if (event.key === 'ArrowUp') {
			this.index.set(decrementIndex(this.index(), this.configs.length));
			return;
		} else if (event.key === 'Enter') {
			this.selectAction(this.configs[this.index()]);
			return;
		}
		let hotKey = event.key;
		event.preventDefault();
		if (event.shiftKey) {
			hotKey = 'Shift+' + hotKey.toLowerCase();
		}
		if (event.ctrlKey) {
			hotKey = 'Ctrl+' + hotKey.toLowerCase();
		}
		const config = this.configsHotkeyMap.get(hotKey);
		if (!config) {
			return;
		}
		this.selectAction(config);
	}
	scrollToActiveElement() {
		const activeElement = this.el.nativeElement.querySelector('.active');

		if (activeElement) {
			activeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
		}
	}
}
